import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { isAuth, fetchUserTenants } from './store/actions/users';
import { Loader } from './utils/tools';
import AdminApp from './admin/AdminApp';
import PublicPackagingUnitPage from './components/publicPackagingUnit';
import { Alert } from 'react-bootstrap';


import MainLayout from './hoc/mainLayout';
import Header from './components/navigation/header';
import Sidebar from './components/navigation/sidebar';

// User & authentification
import Auth from './components/auth';
import Register from './components/user/register';
import AccountVerify from './components/auth/verification';
import NotVerified from './components/auth/notVerified';
import AccountVerified from './components/auth/accountVerified';
import ForgotPassword from './components/auth/forgotPassword';
import ResetPassword from './components/auth/resetPassword';

import Dashboard from './components/dashboard';

// Branch
import Branch from './components/branch';

// Evidence list & wastes
import EvidenceList from './components/evidenceList';
import Detail from './components/evidenceList/detail';

// Branch's Warehouse
import Warehouse from './components/evidenceList/warehouse';

// Branch's Container
import Containers from './components/evidenceList/container';

// Client
import ShowClients from './components/client';

// Profile
import Profile from './components/profile';

// Settings
import Settings from './components/settings';

// Billing
import Billing from './components/billing';

// Consents
import Consents from './components/consents';

// Operational Log
import OperationalLog from './components/operationalLog';
import OperationalLogDetail from './components/operationalLog/detail';

// Statistics
import Statistics from './components/statistics';

// PPWR
import PPWR, { ModuleGate as PpwrModuleGate } from './components/ppwr';

// Waste module gate (upsell for PPWR-only users)
import WasteModuleGate from './components/wasteModuleGate';

// Activity Log
import ActivityLog from './components/activityLog';

// Announcement
import Announcement from './components/announcement';
import AnnouncementDetail from './components/announcement/detail';
import ManualExport from './components/announcement/manual-export';

import Toast from './components/navigation/toast';
import TenantSetupModal from './components/tenantSetup';

import AuthGuard from './hoc/authGuard';
import DynamicTitle from './components/navigation/dynamicTitle';
import Breadcrumbs from './components/navigation/breadcrumbs';

// CSS styles
import './styles/style.css';

const BlockedTenantBanner = () => {
  const activeTenantBlocked = useSelector(state => state.users.data?.activeTenantBlocked);
  const activeTenantName = useSelector(state => {
    const { tenantList, activeTenantId } = state.users.data ?? {};
    if (!tenantList || !activeTenantId) return null;
    return tenantList.find(t => t._id === activeTenantId)?.companyName ?? null;
  });

  if (!activeTenantBlocked) return null;

  return (
    <Alert variant="danger" className="blocked-tenant-banner mb-0 rounded-0 text-center py-2">
      <strong>⛔ Subjekt „{activeTenantName}" je zablokovaný.</strong>{' '}
      Nemôžete vykonávať žiadnu činnosť. Prepnite na iný subjekt alebo kontaktujte administrátora.
    </Alert>
  );
};

const UserRouter = () => {

  // When app starts check user's token
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();

  // Access to users state
  const users = useSelector(state => state.users);
  const hasTenant = useSelector(state => state.users.data?.tenant?.length > 0);
  const tenantListLoaded = useSelector(state => state.users.data?.tenantListLoaded);

  // Re-run whenever the auth flag itself changes (login/logout), not just on mount -
  // signin/register only return basic fields (no isOwner/modules), so switching accounts
  // in the same tab needs a fresh isauth() call to hydrate them correctly.
  useEffect(() => {
    dispatch(isAuth());
  }, [dispatch, users.auth]);

  // Fetch tenant list as soon as auth is confirmed and user has tenants
  useEffect(() => {
    if (users.auth && hasTenant && !tenantListLoaded) {
      dispatch(fetchUserTenants());
    }
  }, [users.auth, hasTenant, tenantListLoaded, dispatch]);

  // Keep loading until tenant status is resolved (prevents API calls before blocked status is known)
  useEffect(() => {
    if (users.auth !== null) {
      if (!hasTenant || tenantListLoaded) {
        setLoading(false);
      }
    }
  }, [users.auth, hasTenant, tenantListLoaded]);

  
  return(
    <>
    { !users.auth ? (
      <>
        <Toast />
        <Routes>
          <Route path="*" element={<Auth />} />
          <Route path="/account/login" element={<Auth/>}/>
          <Route path="/account/register" element={<Register/>}/>
          <Route path="/account/forgot-password" element={<ForgotPassword/>}/>
        </Routes>
      </>
      
    ) : users.data?.role === 'admin' ? (
      <Navigate to="/admin" replace />
    ) : (
      <>
        { loading ? (
          <Loader/>
        ) : (
          <>
            <Toast />
            <TenantSetupModal />
            <Header/>
            <BlockedTenantBanner />

            <div className="custom-sidebar d-none d-sm-block">
                  <Sidebar />
                </div>
                <div className="custom-main-layout">

                  <DynamicTitle />
                  <Breadcrumbs />

                  <MainLayout>
                    <Routes>
                      {/* USER'S VERIFICATION */}
                      <Route path="/verification" element={<AccountVerify/>}/>
                      <Route path="/account/not-verified" element={<NotVerified/>}/>
                      <Route path="/account/verified" element={<AccountVerified/>}/>

                      {/* DASHBOARD */}
                      <Route path="/dashboard" element={<AuthGuard><Dashboard /></AuthGuard>}></Route>

                      {/* Branch - prevádzky sú základná štruktúra firmy, nie súčasť konkrétneho modulu */}
                      <Route path="/branch" element={<AuthGuard><Branch/></AuthGuard>}></Route>

                      {/* EVIDENCE LIST */}
                      <Route path="/evidencelist/" element={<AuthGuard module="waste" fallback={<WasteModuleGate />}><EvidenceList /></AuthGuard>}/>
                      <Route path='/evidencelist/:id' element={<AuthGuard module="waste" fallback={<WasteModuleGate />}><Detail /></AuthGuard>}/>

                      {/* ANNOUNCEMENT */}
                      <Route path="/announcement/" element={<AuthGuard module="waste" fallback={<WasteModuleGate />}><Announcement /></AuthGuard>}/>
                      <Route path="/announcement/detail" element={<AuthGuard module="waste" fallback={<WasteModuleGate />}><AnnouncementDetail /></AuthGuard>}/>
                      <Route path="/announcement/manual-export" element={<AuthGuard module="waste" fallback={<WasteModuleGate />}><ManualExport /></AuthGuard>}/>

                      {/* WAREHOUSE */}
                      <Route path="/warehouse" element={<AuthGuard module="waste" fallback={<WasteModuleGate />}><Warehouse /></AuthGuard>}/>

                      {/* CONTAINER */}
                      <Route path="/container" element={<AuthGuard module="waste" fallback={<WasteModuleGate />}><Containers /></AuthGuard>}/>

                      {/* CLIENT - odberatelia/dodávatelia sa využívajú aj v PPWR, nie iba v odpadovom module */}
                      <Route path="/client/" element={<AuthGuard><ShowClients /></AuthGuard>}/>

                      {/* PROFILE */}
                      <Route path="/profile" element={<AuthGuard requireVerified={false}><Profile /></AuthGuard>}/>

                      {/* SETTINGS */}
                      <Route path="/settings/" element={<AuthGuard requireVerified={false}><Settings /></AuthGuard>}/>

                      {/* BILLING - iba vlastník subjektu */}
                      <Route path="/billing" element={<AuthGuard requireOwner><Billing /></AuthGuard>}/>

                      {/* CONSENTS */}
                      <Route path="/consents" element={<AuthGuard module="waste" fallback={<WasteModuleGate />}><Consents /></AuthGuard>}/>

                      {/* OPERATIONAL LOG */}
                      <Route path="/operational-log" element={<AuthGuard module="operationalLog" fallback={<WasteModuleGate />}><OperationalLog /></AuthGuard>}/>
                      <Route path="/operational-log/:operationalLogId" element={<AuthGuard module="operationalLog" fallback={<WasteModuleGate />}><OperationalLogDetail /></AuthGuard>}/>

                      {/* STATISTICS */}
                      <Route path="/statistics" element={<AuthGuard module="waste" fallback={<WasteModuleGate />}><Statistics /></AuthGuard>}/>

                      {/* PPWR */}
                      <Route path="/ppwr" element={<AuthGuard module="ppwr" fallback={<PpwrModuleGate />}><PPWR /></AuthGuard>}/>

                      {/* ACTIVITY LOG */}
                      <Route path="/activity" element={<AuthGuard><ActivityLog /></AuthGuard>}/>

                      {/* DEFAULT PATH */}
                      <Route path="*" element={
                        <Navigate to={users.data?.role === 'admin' ? '/admin' : '/dashboard'} />
                      } />
                    </Routes>

                  </MainLayout>

                </div>
          </>
        )}
      </>
    )}
    </>
  )
}

const Router = () => {
  const location = useLocation();
  if (location.pathname === '/admin' || location.pathname.startsWith('/admin/')) {
    return <AdminApp />;
  }
  // Obnova/nastavenie hesla musí fungovať aj keď je v prehliadači aktívna iná relácia
  // (napr. owner si otvorí odkaz vo vlastnom prihlásenom prehliadači) - preto mimo auth vetvenia.
  if (location.pathname === '/account/reset-password') {
    return (
      <>
        <Toast />
        <ResetPassword />
      </>
    );
  }
  // Verejná stránka obalu - dostupná bez prihlásenia, preto úplne mimo AuthGuard/UserRouter.
  if (location.pathname.startsWith('/public/packaging/')) {
    return (
      <Routes>
        <Route path="/public/packaging/:tenantId/:publicId" element={<PublicPackagingUnitPage />} />
      </Routes>
    );
  }
  return <UserRouter />;
};

export default Router;
